import { makeRequest } from "@/util";

import type {
  Survey,
  Question,
  SurveyResultsData,
  QuestionResponseSummary,
  SurveyStatus,
  QuestionType,
  DistributionData,
  RatingData,
  OpenTextData,
  NpsData,
} from "./types";

// These mirror the JSON shapes returned by the backend. They are
// intentionally kept local to this file — consumers use the
// frontend Survey/Question types from ./types.ts.
type BackendQuestionOptions =
  | { type: "single_choice"; choices: string[] }
  | { type: "open_text"; placeholder: string }
  | { type: "rating"; lowLabel: string; highLabel: string }
  | { type: "yes_no" }
  | { type: "nps" };

type BackendQuestion = {
  id: string;
  surveyId: string;
  position: number;
  type: QuestionType;
  text: string;
  options: BackendQuestionOptions;
  createdAt: string;
  updatedAt: string;
};

type BackendSurvey = {
  id: string;
  envId: string;
  name: string;
  description: string;
  status: SurveyStatus;
  version: number;
  urlMatchPattern: string;
  urlMatchType: string;
  idleTimeSeconds: number;
  startDate: string | null;
  endDate: string | null;
  questions: BackendQuestion[];
  responseCount: number;
  createdAt: string;
  updatedAt: string;
  sourceConversationId: string | null;
};

type BackendQuestionSummary = {
  questionId: string;
  questionIndex: number;
  questionType: QuestionType;
  questionPrompt: string;
  totalResponses: number;
  data: unknown;
};

type BackendResults = {
  surveyId: string;
  totalResponses: number;
  completionRate: number | null;
  questionSummaries: BackendQuestionSummary[];
};

type GetSurveysResponse = { surveys: BackendSurvey[] };
type CreateSurveyResponse = { survey: BackendSurvey };
type UpdateSurveyResponse = { survey: BackendSurvey };
type DeleteSurveyResponse = { success: boolean };
type PublishSurveyResponse = { survey: BackendSurvey };
type ArchiveSurveyResponse = { survey: BackendSurvey };
type UnpublishSurveyResponse = { survey: BackendSurvey };
type GetSurveyResultsResponse = { results: BackendResults };

function parseBackendQuestion(raw: BackendQuestion): Question {
  const base = { id: raw.id, text: raw.text, position: raw.position };

  switch (raw.type) {
    case "single_choice": {
      const opts = raw.options as { type: "single_choice"; choices: string[] };
      return { ...base, type: "single_choice", options: opts.choices };
    }
    case "open_text": {
      const opts = raw.options as { type: "open_text"; placeholder: string };
      return { ...base, type: "open_text", placeholder: opts.placeholder };
    }
    case "rating": {
      const opts = raw.options as {
        type: "rating";
        lowLabel: string;
        highLabel: string;
      };
      return {
        ...base,
        type: "rating",
        scale: 5,
        lowLabel: opts.lowLabel,
        highLabel: opts.highLabel,
      };
    }
    case "yes_no":
      return { ...base, type: "yes_no" };
    case "nps":
      return { ...base, type: "nps" };
  }
}

function parseBackendSurvey(raw: BackendSurvey): Survey {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description,
    status: raw.status,
    questions: raw.questions.map(parseBackendQuestion),
    urlMatchCondition: {
      pattern: raw.urlMatchPattern,
      matchType: raw.urlMatchType as Survey["urlMatchCondition"]["matchType"],
    },
    idleTimeSeconds: raw.idleTimeSeconds,
    startDate: raw.startDate ? new Date(raw.startDate) : null,
    endDate: raw.endDate ? new Date(raw.endDate) : null,
    responseCount: raw.responseCount,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    sourceConversationId: raw.sourceConversationId ?? null,
  };
}

function parseBackendResults(raw: BackendResults): SurveyResultsData {
  return {
    surveyId: raw.surveyId,
    totalResponses: raw.totalResponses,
    completionRate: raw.completionRate ?? null,
    questionSummaries: raw.questionSummaries.map(
      (qs): QuestionResponseSummary => {
        const base = {
          questionId: qs.questionId,
          questionIndex: qs.questionIndex,
          questionPrompt: qs.questionPrompt,
          totalResponses: qs.totalResponses,
        };

        switch (qs.questionType) {
          case "single_choice":
          case "yes_no":
            return {
              ...base,
              questionType: qs.questionType,
              data: qs.data as DistributionData,
            };
          case "rating":
            return {
              ...base,
              questionType: "rating",
              data: qs.data as RatingData,
            };
          case "open_text":
            return {
              ...base,
              questionType: "open_text",
              data: qs.data as OpenTextData,
            };
          case "nps":
            return { ...base, questionType: "nps", data: qs.data as NpsData };
        }
      },
    ),
  };
}

function serializeQuestionForBackend(q: Question) {
  const base = { type: q.type, text: q.text };

  switch (q.type) {
    case "single_choice":
      return {
        ...base,
        options: { type: "single_choice" as const, choices: q.options },
      };
    case "open_text":
      return {
        ...base,
        options: { type: "open_text" as const, placeholder: q.placeholder },
      };
    case "rating":
      return {
        ...base,
        options: {
          type: "rating" as const,
          lowLabel: q.lowLabel,
          highLabel: q.highLabel,
        },
      };
    case "yes_no":
      return { ...base, options: { type: "yes_no" as const } };
    case "nps":
      return { ...base, options: { type: "nps" as const } };
  }
}

export type CreateSurveyParams = Omit<
  Survey,
  "id" | "createdAt" | "updatedAt" | "responseCount" | "status"
>;

export type UpdateSurveyParams = Partial<
  Omit<Survey, "id" | "createdAt" | "updatedAt" | "responseCount" | "status">
>;

// INTENTIONAL OMISSIONS (CE-1283):
//   - `appearance` (position + theme): captured in the builder for the
//     upcoming survey-widget redesign but the backend doesn't persist it
//     yet. Add the field here when the backend schema lands.
//   - `responseLimit` (mode + maxResponses): same story — the UI exposes
//     it on the Behavior step but we don't send it over the wire until
//     the backend supports it.
//   - `position` (legacy top-level field): deprecated in favor of
//     `appearance.position`; dropped intentionally.
// These are kept out of the serializers rather than sent as ignored
// payload so the network contract stays honest about what we persist.
function serializeSurveyForCreate(data: CreateSurveyParams) {
  return {
    name: data.name,
    description: data.description,
    questions: data.questions.map(serializeQuestionForBackend),
    urlMatchPattern: data.urlMatchCondition.pattern,
    urlMatchType: data.urlMatchCondition.matchType,
    idleTimeSeconds: data.idleTimeSeconds,
    startDate: data.startDate ? data.startDate.toISOString() : null,
    endDate: data.endDate ? data.endDate.toISOString() : null,
    sourceConversationId: data.sourceConversationId ?? undefined,
  };
}

// See serializeSurveyForCreate for the intentional-omission list
// (appearance, responseLimit, position); kept in sync here.
function serializeSurveyForUpdate(data: UpdateSurveyParams) {
  return {
    ...(data.name !== undefined && { name: data.name }),
    ...(data.description !== undefined && { description: data.description }),
    ...(data.questions !== undefined && {
      questions: data.questions.map(serializeQuestionForBackend),
    }),
    ...(data.urlMatchCondition !== undefined && {
      urlMatchPattern: data.urlMatchCondition.pattern,
      urlMatchType: data.urlMatchCondition.matchType,
    }),
    ...(data.idleTimeSeconds !== undefined && {
      idleTimeSeconds: data.idleTimeSeconds,
    }),
    ...(data.startDate !== undefined && {
      startDate: data.startDate ? data.startDate.toISOString() : null,
    }),
    ...(data.endDate !== undefined && {
      endDate: data.endDate ? data.endDate.toISOString() : null,
    }),
  };
}

export async function fetchSurveys(): Promise<Survey[]> {
  const res = await makeRequest<GetSurveysResponse>(
    "get_surveys",
    {},
    {
      validateStatus: (status) => {
        // 404 statuses are harmless. They might arise when the user doesn't
        // have access to the Freshpaint AI feature.
        return (status >= 200 && status < 300) || status === 404;
      },
    },
  );
  const surveys = Array.isArray(res.data?.surveys) ? res.data.surveys : [];
  return surveys.map(parseBackendSurvey);
}

export async function createSurvey(data: CreateSurveyParams): Promise<Survey> {
  const res = await makeRequest<CreateSurveyResponse>(
    "create_survey",
    serializeSurveyForCreate(data),
  );
  return parseBackendSurvey(res.data.survey);
}

export async function updateSurvey(
  surveyId: string,
  data: UpdateSurveyParams,
): Promise<Survey> {
  const res = await makeRequest<UpdateSurveyResponse>("update_survey", {
    surveyId,
    ...serializeSurveyForUpdate(data),
  });
  return parseBackendSurvey(res.data.survey);
}

export async function deleteSurvey(surveyId: string): Promise<void> {
  await makeRequest<DeleteSurveyResponse>("delete_survey", { surveyId });
}

export async function publishSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<PublishSurveyResponse>("publish_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

export async function archiveSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<ArchiveSurveyResponse>("archive_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

export async function unpublishSurvey(surveyId: string): Promise<Survey> {
  const res = await makeRequest<UnpublishSurveyResponse>("unpublish_survey", {
    surveyId,
  });
  return parseBackendSurvey(res.data.survey);
}

export async function fetchSurveyResults(
  surveyId: string,
): Promise<SurveyResultsData> {
  const res = await makeRequest<GetSurveyResultsResponse>(
    "get_survey_results",
    { surveyId },
  );
  return parseBackendResults(res.data.results);
}
